// src/utils/companyClient.js - Company management functions

import { supabase } from './supabaseClient';

/* ================================
   COMPANY DATA FUNCTIONS
================================ */

// Get complete company details
export const getCompanyDetails = async (companyId) => {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('id', companyId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching company details:', error);
    throw error;
  }
};

// Update company profile information
export const updateCompanyProfile = async (companyId, updates) => {
  try {
    // Validate the updates object
    const allowedFields = [
      'name',
      'description', 
      'website',
      'industry',
      'state',
      'size'
    ];

    // Filter to only allowed fields
    const filteredUpdates = {};
    Object.keys(updates).forEach(key => {
      if (allowedFields.includes(key)) {
        filteredUpdates[key] = updates[key];
      }
    });

    // Validate required fields
    if (filteredUpdates.name && filteredUpdates.name.trim().length === 0) {
      throw new Error('Company name cannot be empty');
    }

    // Validate website URL format if provided
    if (filteredUpdates.website && filteredUpdates.website.trim()) {
      const website = filteredUpdates.website.trim();
      if (!website.startsWith('http://') && !website.startsWith('https://')) {
        filteredUpdates.website = 'https://' + website;
      }
    }

    // Clean up empty strings to null for optional fields
    ['description', 'website', 'industry', 'state', 'size'].forEach(field => {
      if (filteredUpdates[field] === '') {
        filteredUpdates[field] = null;
      }
    });

    // Add updated_at timestamp (will be handled by trigger, but explicit is good)
    filteredUpdates.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('companies')
      .update(filteredUpdates)
      .eq('id', companyId)
      .select(`
        id,
        name,
        description,
        website,
        industry,
        state,
        org_type,
        size,
        invite_code,
        created_at,
        updated_at
      `)
      .single();

    if (error) {
      console.error('❌ Database error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('❌ Error updating company profile:', error);
    
    // Provide user-friendly error messages
    if (error.message.includes('name_unique') || error.message.includes('duplicate')) {
      throw new Error('A company with this name already exists');
    } else if (error.message.includes('valid_website')) {
      throw new Error('Please enter a valid website URL');
    } else if (error.message.includes('check constraint')) {
      throw new Error('Invalid data provided. Please check your inputs.');
    } else {
      throw new Error(error.message || 'Failed to update company profile');
    }
  }
};

// Get company statistics and metrics
export const getCompanyStats = async (companyId) => {
  try {
    // Get team member count
    const { count: memberCount, error: memberError } = await supabase
      .from('memberships')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId);

    if (memberError) throw memberError;

    // Get project count (assuming you have a projects table)
    const { count: projectCount, error: projectError } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId);

    // Don't throw error if projects table doesn't exist yet
    const safeProjectCount = projectError ? 0 : projectCount;

    return {
      memberCount: memberCount || 0,
      projectCount: safeProjectCount || 0,
      // Add more stats as needed
    };
  } catch (error) {
    console.error('Error fetching company stats:', error);
    throw error;
  }
};

/* ================================
   TEAM MANAGEMENT FUNCTIONS
================================ */

// Get all company members with their profiles
export const getCompanyMembers = async (companyId) => {
  try {
    const { data, error } = await supabase
      .from('company_members')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error from Supabase:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform data - the view already flattens everything
    const transformedData = data.map(member => ({
      // Membership fields (already at top level in view)
      id: member.id,
      user_id: member.user_id,
      company_id: member.company_id,
      role: member.role,
      created_at: member.created_at,
      updated_at: member.updated_at,
      
      // Create profile object from flattened view data
      profile: {
        id: member.id,
        user_id: member.user_id,
        first_name: member.first_name,        // ✅ Already at top level
        last_name: member.last_name,          // ✅ Already at top level
        company_title: member.company_title,   // ✅ Already at top level
        phone: member.phone,                   // ✅ Already at top level
        avatar_url: member.avatar_url,         // ✅ Already at top level
        onboarding_completed: member.onboarding_completed, // ✅ Already at top level
        email: member.email // Add email to profile object
      },
      
      // Use pre-computed fields from the view
      full_name: member.full_name,           // ✅ Already computed in view
      display_name: member.display_name,     // ✅ Already computed in view
      company_name: member.company_name      // ✅ Already in view
    }));

    return transformedData;
  } catch (error) {
    console.error('❌ Error fetching company members:', error);
    throw error;
  }
};

// Invite a new team member
export const inviteTeamMember = async (companyId, email, role = 'member') => {
  try {
    // This would typically involve:
    // 1. Creating a pending invitation record
    // 2. Sending an email invitation
    // 3. Providing an invite link with the company invite code

    // For now, we'll create a simple invitation system
    const { data, error } = await supabase
      .from('pending_invitations')
      .insert({
        company_id: companyId,
        email: email.toLowerCase(),
        role: role,
        invited_by: (await supabase.auth.getUser()).data.user?.id,
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
      })
      .select()
      .single();

    if (error) throw error;

    // TODO: Send email invitation here
    // await sendInvitationEmail(email, companyId, data.id);

    return data;
  } catch (error) {
    console.error('Error inviting team member:', error);
    throw error;
  }
};

// Update member role
export const updateMemberRole = async (membershipId, newRole) => {
  try {
    const { data, error } = await supabase
      .from('memberships')
      .update({ 
        role: newRole,
        updated_at: new Date().toISOString()
      })
      .eq('id', membershipId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
};

// Remove team member
export const removeMember = async (membershipId) => {
  try {
    const { error } = await supabase
      .from('memberships')
      .delete()
      .eq('id', membershipId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing member:', error);
    throw error;
  }
};

// Get pending invitations
export const getPendingInvitations = async (companyId) => {
  try {
    const { data, error } = await supabase
      .from('pending_invitations')
      .select('*')
      .eq('company_id', companyId)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching pending invitations:', error);
    return []; // Return empty array on error
  }
};

// Cancel invitation
export const cancelInvitation = async (invitationId) => {
  try {
    const { error } = await supabase
      .from('pending_invitations')
      .delete()
      .eq('id', invitationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error canceling invitation:', error);
    throw error;
  }
};

/* ================================
   SUBSCRIPTION FUNCTIONS
================================ */

export const getCompanySubscription = async (companyId) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const inactiveSubscription = {
    id: null,
    status: 'inactive',
    active: false,
    access_allowed: false,
    plan: null,
    plan_name: 'Manual Subscription',
    billing_interval: null,
    billing_interval_display: null,
    billing_start_date: null,
    billing_start_date_display: null,
    billing_amount_cents: null,
    billing_currency: null,
    billing_method: null,
    trial_end: null,
    trial_end_display: null,
    is_trialing: false,
    trial_days_remaining: null,
    current_period_start: null,
    current_period_start_display: null,
    current_period_end: null,
    next_billing_date: null,
    next_billing_date_display: null,
    current_period_end_display: null,
    next_payment_display: null,
    notes: null,
  };

  try {
    const { data, error } = await supabase
      .rpc('get_company_entitlement', {
        company_id_input: companyId
      });

    if (error) {
      console.error('[getCompanySubscription] RPC error:', error);
      return inactiveSubscription;
    }

    if (!data || data.length === 0) {
      return inactiveSubscription;
    }

    const entitlement = data[0];
    const status = entitlement.access_allowed
      ? entitlement.active ? 'active' : 'trialing'
      : entitlement.trial_ends_at ? 'expired' : 'inactive';
    const billingIntervalDisplay = entitlement.billing_interval === 'year'
      ? 'annually'
      : entitlement.billing_interval === 'month'
        ? 'monthly'
        : null;

    return {
      id: entitlement.company_id,
      status,
      active: entitlement.active,
      access_allowed: entitlement.access_allowed,
      plan: entitlement.plan,
      plan_name: entitlement.plan || 'Manual Subscription',
      billing_interval: entitlement.billing_interval,
      billing_interval_display: billingIntervalDisplay,
      billing_start_date: entitlement.billing_start_date,
      billing_start_date_display: formatDate(entitlement.billing_start_date),
      billing_amount_cents: entitlement.billing_amount_cents,
      billing_currency: entitlement.billing_currency,
      billing_method: entitlement.billing_method,
      trial_end: entitlement.trial_ends_at,
      trial_end_display: formatDate(entitlement.trial_ends_at),
      is_trialing: entitlement.is_trialing,
      trial_days_remaining: entitlement.trial_days_remaining,
      current_period_start: entitlement.current_period_start,
      current_period_start_display: formatDate(entitlement.current_period_start),
      current_period_end: entitlement.current_period_end,
      current_period_end_display: formatDate(entitlement.current_period_end),
      next_billing_date: entitlement.next_billing_date,
      next_billing_date_display: formatDate(entitlement.next_billing_date),
      next_payment_display: formatDate(entitlement.next_billing_date),
      notes: entitlement.notes,
      created_at: entitlement.created_at,
      updated_at: entitlement.updated_at,
    };
    
  } catch (error) {
    console.error('❌ Error in getCompanySubscription:', error);
    return inactiveSubscription;
  }
};

// Get lightweight subscription status (for feature gating)
export const getCompanySubscriptionStatus = async (companyId) => {
  const { data, error } = await supabase
    .rpc('get_company_entitlement', { company_id_input: companyId });
  if (error || !data?.length) return null;
  return data[0].access_allowed ? 'active' : 'inactive';
};

/* ================================
   COMPANY SETTINGS FUNCTIONS
================================ */

// Regenerate company invite code
export const regenerateInviteCode = async (companyId) => {
  try {
    // Generate a new 8-character invite code
    const newInviteCode = Math.random().toString(36).substring(2, 10).toUpperCase();

    const { data, error } = await supabase
      .from('companies')
      .update({ 
        invite_code: newInviteCode,
        updated_at: new Date().toISOString()
      })
      .eq('id', companyId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error regenerating invite code:', error);
    throw error;
  }
};

/* ================================
   UTILITY FUNCTIONS
================================ */

// Check if user has permission for action
export const checkCompanyPermission = async (companyId, userId, requiredRole = 'member') => {
  try {
    const { data, error } = await supabase
      .from('memberships')
      .select('role')
      .eq('company_id', companyId)
      .eq('user_id', userId)
      .single();

    if (error) throw error;

    const roleHierarchy = {
      'viewer': 1,
      'member': 2,
      'admin': 3,
      'owner': 4
    };

    const userLevel = roleHierarchy[data.role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;

    return userLevel >= requiredLevel;
  } catch (error) {
    console.error('Error checking company permission:', error);
    return false;
  }
};

// Get user's role in company
export const getUserCompanyRole = async (companyId, userId) => {
  try {
    const { data, error } = await supabase
      .from('memberships')
      .select('role')
      .eq('company_id', companyId)
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    return data.role;
  } catch (error) {
    console.error('Error fetching user company role:', error);
    return null;
  }
};
